import { useState } from "react";
import { App } from "antd";
import axios from "axios";

import { BACKEND_URL } from "config";
import { useAuth } from "hooks/useAuth";

const axiosInstance = axios.create({
  baseURL: BACKEND_URL,
  withCredentials: true,
});

const useExport = (url, fetchMethod = "POST", displayError = true, resetStateOnFetch = true) => {
  const { message } = App.useApp();
  const { logoutUser } = useAuth();
  const [responseData, setResponseData] = useState({});
  const [errorMessage, setErrorMessage] = useState();

  let onError = () => {};
  if (displayError) {
    onError = (errorMessage) => {
      message.error(errorMessage);
    };
  }

  const doFetch = (requestData) => {

    // Reset state
    if (resetStateOnFetch){
      setResponseData({});
    }

    const fetchApi = () => {
      switch (fetchMethod) {
        case "GET":
          return getApi(url, requestData);
        default:
          return postApi(url, requestData);
      }
    };

    return fetchApi()
      .then((response) => response.data)
      .then((response) => {
        if (!!response.error) {
          setErrorMessage(response.error);
          onError(response.error);
        }
        setResponseData(response.data ?? {});
        return response;
      })
      .catch((error) => {
        if (error.response?.status === 401) {
          logoutUser();
        } else {
          const responseError = error.response?.data?.error ?? error.message;
          setErrorMessage(responseError);
          onError(responseError);
        }
      })
  };

  const doExport = (data, filename, TABLE_FIELDS, FIELD_ORDERS, displayTimestamp = true) => {
    data = reorderField(data, TABLE_FIELDS, FIELD_ORDERS)
    const csvContent = 'data:text/csv;charset=utf-8,' 
        + formatTitles(Object.keys(data[0])).join(',') + '\n' // First row with keys
        + data.map(row => Object.values(row).join(',')).join('\n'); // Data

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', filename + getTimestamp(displayTimestamp) + '.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return { responseData, errorMessage, doFetch, doExport };
};

const getCookie = (key) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${key}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

const postApi = (url, requestData) => {
  return axiosInstance.post(url, requestData, {
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-TOKEN": getCookie("csrf_access_token"),
    },
  });
};

const getApi = (url, requestData) => {
  return axiosInstance.get(url, {
    params: requestData,
  });
};

function formatTitles(titles){

  function removeUnderscore(str){
    return str.replace(/_/g, ' ');
  }

  function toUpper(str){
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  return titles.map(title => toUpper(removeUnderscore(title)))
};

function getTimestamp(displayTimestamp){
  if(displayTimestamp){
    let now = new Date();
    let year = now.getFullYear().toString();
    let month = ('0' + (now.getMonth() + 1)).slice(-2);
    let day = ('0' + now.getDate()).slice(-2);

    return '_' + year + month + day
  }else{
    return ''
  }
}

function reorderField(data, TABLE_FIELDS, FIELD_ORDERS){
  data.forEach(dict => {
    Object.keys(dict).forEach(key => {
      //Remove field that is does not in the TABLE_FIELDS
      if(!TABLE_FIELDS.includes(key)){
        delete dict[key]
      }

      //Pre-process for field that not is string in raw
      if(key === "detected_code") {
        dict[key] = dict[key]?.value ?? "-"
      }
    })

    //Order field according to FIELD_ORDERS
    var orderedDict = {};
    FIELD_ORDERS.forEach(key => {
      if (dict.hasOwnProperty(key)) {
        orderedDict[key] = dict[key];
        delete dict[key]
      }
    });
    Object.assign(dict, orderedDict);
  })

  return data
}

export default useExport;
